import { useCallback, useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatChartData, getFormattedValue, getMeasuresLabel, hasSelectedChartData } from "./utils";
import dayjs from "dayjs";
import {
  CHART_COLOR_PALETTE,
  CHART_DIMENSIONS,
  CHART_GRID,
  DATE_FORMAT,
  getLineConfig,
  getYAxisConfig,
} from "../../config/charts/sensorChart.config";
import NoChartDataEmptyState from "../emptyStates/NoChartDataEmptyState.component";

const SensorChart = ({ chartData, selectedMeasures, sensors, isLoading, error }) => {
  const formattedChartData = useMemo(() => {
    const selectedData = Object.fromEntries(
      selectedMeasures.map((key) => [key, chartData?.[key] || []]),
    );
    return formatChartData(selectedData);
  }, [chartData, selectedMeasures]);
  const hasData = hasSelectedChartData(formattedChartData, selectedMeasures);

  const measureKeyToLabelMap = useMemo(() => {
    const measures = getMeasuresLabel(sensors);
    return measures.reduce((map, measure) => {
      map[measure.value] = measure;
      return map;
    }, {});
  }, [sensors]);

  const formatXAxis = useCallback((timestamp) => {
    return dayjs(timestamp).format(DATE_FORMAT);
  }, []);

  const tooltipFormatter = useCallback(
    (value, name, entry) => {
      const metadata = measureKeyToLabelMap[entry?.dataKey] || measureKeyToLabelMap[name];
      const label = metadata?.label || name;
      return [getFormattedValue(value, name, metadata?.unit), label];
    },
    [measureKeyToLabelMap]
  );

  const legendFormatter = useCallback(
    (value) => measureKeyToLabelMap[value]?.label || value,
    [measureKeyToLabelMap]
  );

  if (!hasData && !isLoading) {
    return error ? null : <NoChartDataEmptyState />;
  }

  return (
    <ResponsiveContainer width="100%" height={CHART_DIMENSIONS.height}>
      <LineChart data={formattedChartData}>
        <CartesianGrid strokeDasharray={CHART_GRID.strokeDasharray} />
        <XAxis
          dataKey="timestamp"
          tickFormatter={formatXAxis}
          angle={CHART_DIMENSIONS.xAxisAngle}
          textAnchor="end"
          height={CHART_DIMENSIONS.xAxisHeight}
        />
        <Tooltip labelFormatter={formatXAxis} formatter={tooltipFormatter} />
        <Legend formatter={legendFormatter} />
        {selectedMeasures.map((measureKey, index) => {
          const color = CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length];
          const yAxisConfig = getYAxisConfig(measureKey, index, color, measureKeyToLabelMap[measureKey]?.unit);
          return <YAxis key={`yAxis-${measureKey}`} {...yAxisConfig} />;
        })}
        {selectedMeasures.map((measureKey, index) => {
          const color = CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length];
          const label = measureKeyToLabelMap[measureKey]?.label || measureKey;
          const lineConfig = getLineConfig(measureKey, label, color);

          return <Line key={measureKey} {...lineConfig} />;
        })}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default SensorChart;
